"use client"

import * as React from "react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"

import { ApiError } from "@/shared/lib/api/http"

export function QueryProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            // Default freshness window. Without this, `staleTime` is 0 so EVERY query refetches on every
            // component mount — navigating fires a burst of redundant requests. 30s keeps data fresh enough
            // (mutations invalidate explicitly, so writes are still reflected immediately) while collapsing
            // the per-navigation mount-refetch storm. Hooks that need a different window still override it.
            staleTime: 30_000,
            // A network blip shouldn't refire every active query on reconnect (pairs with the staleTime above).
            refetchOnReconnect: false,
            retry: (failureCount, error) => {
              if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
                return false
              }
              return failureCount < 1
            },
          },
        },
      })
  )

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  )
}
